import { EmptyState } from "@/components/molecules/empty-state";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/features/utils/i18n";
import { ArrowClockwiseIcon, WarningCircleIcon } from "@phosphor-icons/react";

/**
 * A query that failed, said as such.
 *
 * Without it a page whose request errored rendered its empty branch — "no
 * agent yet", "no FAQ entry" — which is the one message guaranteed to be
 * wrong: the data is there, we just could not read it, and the person is
 * invited to create what already exists. Pass the query's `refetch` as
 * `onRetry`; the button is left out when there is nothing to retry.
 */
export function ErrorState({
  title,
  description,
  onRetry,
  compact,
  className,
}: {
  title?: React.ReactNode;
  description?: React.ReactNode;
  onRetry?: () => unknown;
  compact?: boolean;
  className?: string;
}) {
  const { t } = useTranslation("common");

  return (
    <EmptyState
      role="alert"
      tone="danger"
      icon={WarningCircleIcon}
      title={title ?? t("common.loadError.title")}
      description={description ?? t("common.loadError.description")}
      compact={compact}
      className={className}
      action={
        onRetry && (
          <Button
            variant="outline"
            size={compact ? "sm" : "default"}
            onClick={() => void onRetry()}
          >
            <ArrowClockwiseIcon />
            {t("common.retry")}
          </Button>
        )
      }
    />
  );
}
